"use client";
import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/react";
import type { FieldPath, ThemeType, UseFormReturn } from "@packages/ui";
import { Button, FormField, Loader, Text } from "@packages/ui";
import { Fragment } from "react";
import { useMediaQuery } from "usehooks-ts";

import { Screens } from "../../lib/enums/screens";
import { useRequestMePinExists } from "../../requests/useRequestMePinExists";
import { PinInput } from "../blocks/PinInput";

const cssDivButtonsContainer: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column-reverse",
  gap: theme.spacing.s,
  [`@media ${Screens.Large}`]: {
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    paddingBottom: theme.spacing.xxl,
  },
});
const cssDivSpacer: Interpolation<ThemeType> = (theme) => ({
  height: theme.spacing.l,
  [`@media ${Screens.Large}`]: {
    flexGrow: 1,
  },
});
const cssDivTextContainer: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.s,
});
const cssLabel: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.l,
  paddingBottom: theme.spacing.s,
  paddingTop: theme.spacing.s,
});
const cssTextDescription: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
  margin: 0,
});
const cssTextTitle = {
  margin: 0,
};

type FormWithPin = {
  pin: string;
};

export const CreationPinForm = <FormType extends FormWithPin = FormWithPin>({
  form,
  onForgotPin,
  pinError,
  existsTitle = "kids_profile_enter_pin_title",
  newTitle = "kids_profile_create_pin_title",
  existsSubtitle = "kids_profile_enter_pin_subtitle",
  newSubtitle = "kids_profile_create_pin_subtitle",
}: {
  form: UseFormReturn<FormType>;
  onForgotPin: () => void | Promise<void>;
  pinError?: string;
  existsTitle?: string;
  existsSubtitle?: string;
  newTitle?: string;
  newSubtitle?: string;
}) => {
  const query = useRequestMePinExists();
  const isLarge = useMediaQuery(Screens.Large);
  if (query.isLoading) return <Loader />;

  return (
    <Fragment>
      <label css={cssLabel}>
        <div css={cssDivTextContainer}>
          <Text as="h2" css={cssTextTitle} size="xl" type="title">
            <I18nMessage
              message={query.data?.exists ? existsTitle : newTitle}
            />
          </Text>
          <Text css={cssTextDescription} size="m" type="body">
            <I18nMessage
              message={query.data?.exists ? existsSubtitle : newSubtitle}
            />
          </Text>
        </div>
        <FormField<FormType>
          control={form.control}
          name={"pin" as FieldPath<FormType>}
          render={({ field, fieldState }) => (
            <PinInput
              {...field}
              autoSubmit={query.data?.exists}
              error={fieldState.error?.message ?? pinError}
              onValueChange={field.onChange}
            />
          )}
          showErrorMessage={false}
        />
      </label>
      <div css={cssDivSpacer} />
      <div css={cssDivButtonsContainer}>
        <Button
          disabled={form.formState.isSubmitting || !form.formState.isValid}
          isFullWidth={!isLarge}
          isLoading={form.formState.isSubmitting}
          size="l"
        >
          <I18nMessage message="onboarding_button_continue" />
        </Button>
        {query.data?.exists && (
          <Button
            isFullWidth={!isLarge}
            onClick={onForgotPin}
            size={isLarge ? "l" : "m"}
            type="button"
            variant={isLarge ? "transparentPrimary" : "transparentNeutral"}
          >
            {isLarge ? (
              <I18nMessage message="kids_profile_forgot_pin" />
            ) : (
              <Text size="m" type="body">
                <I18nMessage message="kids_profile_forgot_pin" />
              </Text>
            )}
          </Button>
        )}
      </div>
    </Fragment>
  );
};
